import { Plane, Snowflake, Package } from "lucide-react";
import japanFlagSvg from "@/assets/icons/japan-flag.svg";
import { useI18n } from "@/context/I18nContext";

export function AnimatedJapanShipment() {
  const { t } = useI18n();
  return (
    <div className="w-full h-full min-h-[260px] bg-gradient-to-br from-navy via-navy/95 to-slate-900 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between text-white border border-white/10 shadow-inner">
      {/* Background Grid Pattern & Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(#14b8a6_1px,transparent_1px)] [background-size:16px_16px] opacity-15" />
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-mint/20 rounded-full blur-3xl" />

      {/* Header telemetry badge */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-mint/15 text-mint text-[11px] font-bold uppercase tracking-wider border border-mint/30">
          <img
            src={japanFlagSvg}
            alt={t("common.alt.flagJapan")}
            className="h-3.5 w-5 rounded object-cover shadow-sm border border-white/20"
          />
          {t("productDetail.sv12Japan.visual.badge")}
        </div>
        <span className="text-[10px] font-mono text-white/60">SV12 · JP</span>
      </div>

      {/* Vector Illustration: culture pool in Chikugo -> air freight -> hatchery */}
      <div className="relative z-10 my-4 flex items-center justify-center">
        <svg
          className="w-full max-w-[300px] h-32"
          viewBox="0 0 300 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Chikugo culture pool: concentric raceway rings */}
          <circle cx="48" cy="66" r="26" className="fill-teal/10 stroke-teal stroke-2" />
          <circle
            cx="48"
            cy="66"
            r="18"
            fill="none"
            stroke="#5eead4"
            strokeWidth="0.8"
            opacity="0.6"
          />
          <circle
            cx="48"
            cy="66"
            r="10"
            fill="none"
            stroke="#5eead4"
            strokeWidth="0.8"
            opacity="0.6"
          />
          <circle cx="48" cy="66" r="3.5" fill="#4ade80" className="animate-pulse-glow" />

          {/* Flight path: dashed arc from plant to hatchery */}
          <path
            id="sv12-flight"
            d="M 74 58 Q 150 4 226 58"
            stroke="#5eead4"
            strokeWidth="1.6"
            strokeDasharray="5 6"
            fill="none"
            opacity="0.7"
          />
          {/* Plane travelling the arc (offset-path keeps it on the curve) */}
          <g className="animate-fly-arc">
            <path d="M 10 0 L -6 -5.5 L -2.5 0 L -6 5.5 Z" fill="#ffffff" />
          </g>

          {/* Destination hatchery: tank with rotifer dots */}
          <rect
            x="228"
            y="52"
            width="44"
            height="30"
            rx="6"
            className="fill-teal/10 stroke-teal stroke-2"
          />
          <path
            d="M 232 62 q 5.5 -4 11 0 t 11 0 t 11 0"
            stroke="#5eead4"
            strokeWidth="1"
            fill="none"
            opacity="0.7"
          />
          <circle cx="242" cy="70" r="1.8" fill="#4ade80" className="animate-pulse-glow" />
          <circle cx="252" cy="74" r="1.8" fill="#4ade80" className="animate-pulse-glow" />
          <circle cx="262" cy="69" r="1.8" fill="#4ade80" className="animate-pulse-glow" />

          <text x="48" y="110" textAnchor="middle" fill="#99f6e4" fontSize="9" fontWeight="bold">
            {t("productDetail.sv12Japan.visual.plantLabel")}
          </text>
          <text x="250" y="110" textAnchor="middle" fill="#99f6e4" fontSize="9" fontWeight="bold">
            {t("productDetail.sv12Japan.visual.tankLabel")}
          </text>
        </svg>
      </div>

      {/* Footer telemetry details */}
      <div className="relative z-10 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] font-semibold text-white/80">
        <span className="flex items-center gap-1">
          <Snowflake className="h-3.5 w-3.5 text-mint" />{" "}
          {t("productDetail.sv12Japan.visual.tempLabel")}
        </span>
        <span className="flex items-center gap-1">
          <Package className="h-3.5 w-3.5 text-mint" />{" "}
          {t("productDetail.sv12Japan.visual.boxLabel")}
        </span>
        <span className="flex items-center gap-1 text-mint font-bold">
          <Plane className="h-3.5 w-3.5" /> {t("productDetail.sv12Japan.visual.routeLabel")}
        </span>
      </div>
    </div>
  );
}
